import {
  FileExcelOutlined,
  FileOutlined,
  FilePdfOutlined,
  FileTextOutlined,
  VideoCameraOutlined,
} from '@ant-design/icons';
import styled from '@emotion/styled';
import { Checkbox, Empty } from 'antd';
import { FC } from 'react';

import { AssetKind } from '../../pages/module1-data';
import { PlaylistModule } from '../../pages/playlist-module-data';

const Wrapper = styled('div')`
  grid-column: 1 / -1;
  display: flex;
  flex-direction: column;
  gap: 32px;
  width: 100%;
`;

const ModuleSection = styled('section')`
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const ModuleTitle = styled('h2')`
  margin: 0;
  color: ${({ theme }) => theme.COLORS.WHITE._300};
  font-size: 24px;
`;

const FolderTitle = styled('h3')`
  margin: 0 0 12px;
  color: ${({ theme }) => theme.COLORS.WHITE._600};
  font-size: 17px;
  font-weight: 600;
`;

const Grid = styled('div')`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 18px;
`;

const AssetCard = styled('button')<{ selected: boolean }>`
  min-width: 0;
  min-height: 148px;
  padding: 18px;
  display: grid;
  grid-template-columns: auto 1fr auto;
  grid-template-rows: 1fr auto;
  gap: 12px;
  text-align: left;
  color: ${({ theme }) => theme.COLORS.WHITE._300};
  background: ${({ theme }) => theme.COLORS.BLACK._400};
  border: 1px solid
    ${({ selected, theme }) =>
      selected ? theme.COLORS.ACCENT._200 : theme.COLORS.BLACK._500};
  border-radius: 8px;
  cursor: pointer;
  opacity: ${({ selected }) => (selected ? 1 : 0.68)};

  &:hover {
    opacity: 1;
    border-color: ${({ theme }) => theme.COLORS.ACCENT._200};
  }
`;

const Icon = styled('span')`
  font-size: 30px;
  color: ${({ theme }) => theme.COLORS.ACCENT._200};
`;

const Name = styled('span')`
  min-width: 0;
  align-self: center;
  font-size: 16px;
  font-weight: 600;
  line-height: 1.35;
  overflow-wrap: anywhere;
`;

const Meta = styled('span')`
  grid-column: 1 / -1;
  color: ${({ theme }) => theme.COLORS.BLACK._700};
  font-size: 13px;
`;

const iconByKind: Record<AssetKind, JSX.Element> = {
  video: <VideoCameraOutlined />,
  excel: <FileExcelOutlined />,
  document: <FileTextOutlined />,
  pdf: <FilePdfOutlined />,
  text: <FileTextOutlined />,
  csv: <FileOutlined />,
  file: <FileOutlined />,
};

export const PlaylistModuleAssets: FC<{
  modules: PlaylistModule[];
  selectedKeys: string[];
  onToggle: (s3Key: string) => void;
}> = ({ modules, selectedKeys, onToggle }) => (
  <Wrapper>
    {modules
      .filter((module) => module.folders.length > 0)
      .map((module) => (
        <ModuleSection key={module.id}>
          <ModuleTitle>{module.name}</ModuleTitle>
          {module.folders.map((folder) => (
            <div key={folder.id}>
              <FolderTitle>{folder.name}</FolderTitle>
              <Grid>
                {folder.files.map((file) => {
                  const selected = selectedKeys.includes(file.s3Key);
                  return (
                    <AssetCard
                      type="button"
                      key={file.s3Key}
                      selected={selected}
                      onClick={() => onToggle(file.s3Key)}
                    >
                      <Icon>{iconByKind[file.kind]}</Icon>
                      <Name>{file.name}</Name>
                      <Checkbox checked={selected} />
                      <Meta>
                        {file.kind.toUpperCase()} · {file.size}
                      </Meta>
                    </AssetCard>
                  );
                })}
              </Grid>
            </div>
          ))}
        </ModuleSection>
      ))}
    {modules.every((module) => module.folders.length === 0) && <Empty />}
  </Wrapper>
);
